'use client';

/**
 * RelatedProducts — "Productos relacionados" section rendered at the
 * bottom of the public product detail page (M-9).
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item M-9.
 *
 * Behavior:
 *   - Fetches up to 8 products in the same category via the existing
 *     `api.market.listProducts({ category, limit: 8, page: 1 })`
 *     endpoint (the public catalog — single source of truth,
 *     CR-CHECKLIST §H2: no parallel "related" endpoint, no parallel
 *     card implementation; re-uses `<ProductCard />` verbatim).
 *   - Filters out the current product (by `productId`).
 *   - Renders up to 4 related products in a horizontal scroll rail
 *     (same pattern as `<RecentlyViewedRail />` — M-7 — fixed-width
 *     cards 220px, snap-x mandatory).
 *   - Shows an empty-state card "No hay productos relacionados" when
 *     no other products exist in the category OR the fetch fails
 *     (fail-graceful: related products are a "nice-to-have"; the main
 *     product payload is what the buyer came for — we never bubble
 *     the error to the page-level error boundary).
 *
 * Why a client component (not a Server Component)?
 *   The product detail page is a Server Component (SSR + ISR for SEO).
 *   `api.market.listProducts` uses `apiFetch` which transitively
 *   touches `tokenStore` (browser localStorage). Although the call
 *   passes `auth: false` (no token access), keeping the fetch
 *   client-side mirrors the `<ProductQuestions />` (F-004b) +
 *   `<RecentlyViewedRail />` (M-7) pattern + avoids coupling the
 *   server ISR cache (60s) to a second backend roundtrip.
 *
 * §6 Anti-Invención:
 *   - NO `as any` (P-041).
 *   - NO `console.*` (P-042).
 *   - ARIA (P-044): section `aria-label`, rail `role="list"`, each
 *     card wrapper `role="listitem"`.
 *   - The empty-state + heading strings are VERBATIM from the M-9
 *     spec ("Productos relacionados", "No hay productos
 *     relacionados") — DO NOT rephrase.
 */

import { useEffect, useState } from 'react';
import { Sparkles, Package } from 'lucide-react';
import { ProductCard } from '@/components/market/product-card';
import { api } from '@/lib/api-client';
import type { PublicProductResponse } from '@profileia/shared';

// Maximum number of related cards rendered in the rail (per the M-9
// spec: "Renders up to 4 related products"). We fetch 8 to leave
// headroom for the current-product filter (if the current product is
// in the result set, we still have 7 candidates to pick 4 from).
const MAX_RELATED = 4;

export interface RelatedProductsProps {
  /** Category slug (e.g., "moda", "tecnologia") — drives the fetch. */
  category: string;
  /** The current product's ID — filtered out of the result set. */
  currentProductId: string;
  /**
   * The current product's storeSlug. Accepted for API symmetry + future
   * use (e.g., a "more from this seller" sort, or a same-store-first
   * ranking). NOT used in the current filter implementation — the M-9
   * spec calls for category-based related products. Kept as a required
   * prop so the parent page's contract is explicit + future-enrichable
   * without a breaking change.
   */
  storeSlug: string;
}

export function RelatedProducts({
  category,
  currentProductId,
  // `storeSlug` is accepted but intentionally unused in the current
  // filter — see the JSDoc on the prop. The underscore prefix marks it
  // as intentionally unused (suppresses `@typescript-eslint/no-unused-vars`).
  storeSlug: _storeSlug,
}: RelatedProductsProps) {
  // `null` = fetch in-flight (render nothing — avoids a flash of
  // empty-state while the request is resolving). `[]` = fetch
  // completed with zero related products (render the empty state).
  const [related, setRelated] = useState<PublicProductResponse[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Reset to `null` when the deps change so a category switch
    // (e.g., navigating between products in different categories via
    // client-side routing) re-enters the loading state cleanly.
    setRelated(null);

    api.market
      .listProducts({ category, limit: 8, page: 1 })
      .then((res) => {
        if (cancelled) return;
        const filtered = (res.products ?? [])
          .filter((p) => p.productId !== currentProductId)
          .slice(0, MAX_RELATED);
        setRelated(filtered);
      })
      .catch(() => {
        // Fail-graceful: render the empty state so the product detail
        // page still loads. Related products are non-critical.
        if (cancelled) return;
        setRelated([]);
      });

    return () => {
      cancelled = true;
    };
  }, [category, currentProductId]);

  // Fetch in-flight — render nothing to avoid a flash of empty-state.
  if (related === null) return null;

  return (
    <section className="mt-8" aria-label="Productos relacionados">
      <h2 className="mb-3 flex items-center gap-2 text-xl font-semibold">
        <Sparkles className="size-5 text-primary" aria-hidden="true" />
        Productos relacionados
      </h2>

      {related.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <Package
            className="size-8 text-muted-foreground/40 mx-auto mb-2"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">
            No hay productos relacionados
          </p>
        </div>
      ) : (
        <div
          role="list"
          className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'thin' }}
        >
          {related.map((p) => (
            <div
              key={p.productId}
              role="listitem"
              className="snap-start shrink-0 w-[220px] max-w-[220px]"
            >
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
